/**
 * Pure helper functions for the Accounting Recurring transactions module.
 *
 * Everything in here is stateless and side-effect free. These
 * helpers are shared between the page composer, hooks, and
 * display components. When the backend ships, nothing in this
 * file changes; only mock data in recurringMockData.ts is swapped
 * for API responses.
 */

import type {
  RecurringTemplate,
  RecurringFilter,
  RecurringSortField,
  RecurringSortDirection,
  RecurringSchedule,
  RecurringTxnType,
  RecurringParty,
} from "./recurringTypes";
import { FREQUENCY_LABELS, MODE_LABELS } from "./recurringMockData";

/**
 * Filter templates by search query + template type (mode) + transaction type.
 */
export function filterRecurring(
  templates: RecurringTemplate[],
  filter: RecurringFilter,
): RecurringTemplate[] {
  const q = filter.search.trim().toLowerCase();

  return templates.filter((t) => {
    if (filter.templateType !== "all" && t.mode !== filter.templateType) return false;
    if (filter.transactionType !== "all" && t.txnType !== filter.transactionType) return false;
    if (!q) return true;
    return matchesSearch(t, q);
  });
}

/**
 * Whether a template's name, party label, category, or memo contain
 * the lowercased search query.
 */
export function matchesSearch(t: RecurringTemplate, lowerQuery: string): boolean {
  if (t.name.toLowerCase().includes(lowerQuery)) return true;
  if (getPartyLabel(t).toLowerCase().includes(lowerQuery)) return true;
  if (t.transaction.category?.toLowerCase().includes(lowerQuery)) return true;
  if (t.transaction.memo?.toLowerCase().includes(lowerQuery)) return true;
  return false;
}

/**
 * Sort templates by the given field + direction. Returns a new
 * array; does not mutate the input.
 */
export function sortRecurring(
  templates: RecurringTemplate[],
  sortBy: RecurringSortField,
  direction: RecurringSortDirection,
): RecurringTemplate[] {
  const multiplier = direction === "asc" ? 1 : -1;
  const sorted = [...templates];

  sorted.sort((a, b) => {
    switch (sortBy) {
      case "name":
        return a.name.localeCompare(b.name) * multiplier;
      case "amount":
        return (a.transaction.amount - b.transaction.amount) * multiplier;
      case "mode":
        return a.mode.localeCompare(b.mode) * multiplier;
      case "nextDate": {
        const aTime = a.nextDate ? new Date(a.nextDate).getTime() : Infinity;
        const bTime = b.nextDate ? new Date(b.nextDate).getTime() : Infinity;
        return (aTime - bTime) * multiplier;
      }
      case "previousDate": {
        const aTime = a.previousDate ? new Date(a.previousDate).getTime() : 0;
        const bTime = b.previousDate ? new Date(b.previousDate).getTime() : 0;
        return (aTime - bTime) * multiplier;
      }
      default:
        return 0;
    }
  });

  return sorted;
}

/**
 * Format a date as "Nov 1, 2026" style. Returns "never" for null.
 */
export function formatDateLong(dateString: string | null): string {
  if (!dateString) return "never";
  const d = new Date(dateString);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Format a date relative to today: "in 11 days", "today",
 * "yesterday", "20 days ago", "in 2 months". Returns null for
 * null input so the caller can decide what to render.
 */
export function formatDateRelative(dateString: string | null): string | null {
  if (!dateString) return null;
  const then = new Date(dateString).getTime();
  const now = Date.now();
  const diffMs = then - now;
  const diffDays = Math.round(diffMs / 86400000);

  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays === -1) return "yesterday";
  if (diffDays > 1 && diffDays < 7) return `in ${diffDays} days`;
  if (diffDays > 6 && diffDays < 30) return `in ${diffDays} days`;
  if (diffDays >= 30 && diffDays < 365) {
    const months = Math.round(diffDays / 30);
    return months === 1 ? "in 1 month" : `in ${months} months`;
  }
  if (diffDays < -1 && diffDays > -30) return `${Math.abs(diffDays)} days ago`;
  if (diffDays <= -30 && diffDays > -365) {
    const months = Math.round(Math.abs(diffDays) / 30);
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }
  if (diffDays >= 365) {
    const years = Math.round(diffDays / 365);
    return years === 1 ? "in 1 year" : `in ${years} years`;
  }
  const years = Math.round(Math.abs(diffDays) / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

/**
 * Whether a date falls within the next N days (positive meaning
 * "coming up soon"). Used by the Next Date column to decide if
 * the row shows an amber "coming up" indicator.
 */
export function isDateSoon(dateString: string | null, days: number): boolean {
  if (!dateString) return false;
  const then = new Date(dateString).getTime();
  const now = Date.now();
  const diffMs = then - now;
  const diffDays = diffMs / 86400000;
  return diffDays > 0 && diffDays <= days;
}

/**
 * Describe the schedule's interval as a short readable string.
 * Examples: "Monthly, 1st of month", "Biweekly, every other Friday",
 * "Quarterly, start of quarter", "Annually, March 1".
 */
export function formatIntervalLabel(schedule: RecurringSchedule): {
  primary: string;
  secondary: string;
} {
  const primary = FREQUENCY_LABELS[schedule.frequency];
  let secondary = "";

  switch (schedule.frequency) {
    case "monthly":
      if (schedule.dayOfMonth === "last") {
        secondary = "End of month";
      } else if (schedule.dayOfMonth) {
        secondary = `${ordinal(schedule.dayOfMonth)} of month`;
      }
      break;
    case "weekly":
    case "biweekly":
      if (schedule.dayOfWeek !== undefined) {
        const days = [
          "Sunday", "Monday", "Tuesday", "Wednesday",
          "Thursday", "Friday", "Saturday",
        ];
        secondary =
          schedule.frequency === "biweekly"
            ? `Every other ${days[schedule.dayOfWeek]}`
            : `Every ${days[schedule.dayOfWeek]}`;
      }
      break;
    case "quarterly":
      if (schedule.dayOfMonth === "last") {
        secondary = "End of quarter";
      } else if (schedule.dayOfMonth === 1) {
        secondary = "Start of quarter";
      } else if (schedule.dayOfMonth) {
        secondary = `${ordinal(schedule.dayOfMonth)} of quarter`;
      }
      break;
    case "annually":
      if (schedule.dayOfMonth) {
        secondary = formatDateLong(schedule.startDate);
      }
      break;
    case "semi_annually":
      secondary = "Twice yearly";
      break;
    case "daily":
    case "custom":
      secondary = "";
      break;
  }

  return { primary, secondary };
}

/**
 * Pretty ordinal: 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th", etc.
 */
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Readable label for the Customer/Supplier column based on the
 * party type. Returns "(internal)" for journal entries.
 */
export function getPartyLabel(t: RecurringTemplate): string {
  const p = t.party;
  switch (p.type) {
    case "supplier":
      return p.supplierName ?? "";
    case "customer":
      return p.customerName ?? "";
    case "internal":
      return "(internal)";
    case "transfer":
      return "Account transfer";
  }
}

/**
 * Column header text for the party column, which varies by txn
 * type: Supplier for expenses and bills, Customer for invoices,
 * Reference for everything else.
 */
export function getPartyColumnLabel(txnType: RecurringTxnType): string {
  switch (txnType) {
    case "expense":
    case "bill":
      return "Supplier";
    case "invoice":
      return "Customer";
    case "journal":
    case "transfer":
      return "Reference";
  }
}

/**
 * Format a monetary amount as "4,200.00". Does NOT prefix with
 * a currency symbol; use <AmountCell> for currency-aware display.
 */
export function formatAmount(amount: number): string {
  return amount.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Compute the next N occurrence dates after a given start date,
 * based on the schedule. Used by the Builder preview section
 * (PR 11) to show "Next 3 occurrences". Returns ISO date strings.
 *
 * Simplified implementation; a full version handles month-end
 * edge cases (Feb 31 → Feb 28/29), DST shifts, holidays, etc.
 */
export function calculateNextOccurrences(
  schedule: RecurringSchedule,
  startFrom: string,
  count: number,
): string[] {
  const out: string[] = [];
  const base = new Date(startFrom);
  const interval = schedule.interval ?? 1;

  for (let i = 0; i < count; i++) {
    const d = new Date(base);
    switch (schedule.frequency) {
      case "daily":
        d.setDate(d.getDate() + i * interval);
        break;
      case "weekly":
        d.setDate(d.getDate() + i * 7 * interval);
        break;
      case "biweekly":
        d.setDate(d.getDate() + i * 14);
        break;
      case "monthly":
        d.setMonth(d.getMonth() + i * interval);
        break;
      case "quarterly":
        d.setMonth(d.getMonth() + i * 3 * interval);
        break;
      case "semi_annually":
        d.setMonth(d.getMonth() + i * 6);
        break;
      case "annually":
        d.setFullYear(d.getFullYear() + i * interval);
        break;
      case "custom":
        d.setDate(d.getDate() + i);
        break;
    }
    out.push(d.toISOString().slice(0, 10));
  }

  return out;
}

/**
 * Short label for the status chip (unused in the main table but
 * handy for the builder preview and future bulk-actions bar).
 */
export function getStatusLabel(t: RecurringTemplate): string {
  if (t.status === "paused") return "Paused";
  if (t.status === "ended") return "Ended";
  return MODE_LABELS[t.mode];
}

/**
 * Avoid unused-import warnings for types surfaced via return
 * types elsewhere in the module.
 */
export type _PartyTypeHint = RecurringParty;